import React, { useCallback, useEffect, useRef, useState } from 'react';

import BudgetByDimensionReportTable from './BudgetByDimensionReportTable';
import { previewCriteriaCacheKey, useBudgetByDimensionReportContext } from './context/BudgetByDimensionReportContext';
import { BudgetByDimensionReportTableProvider } from './context/BudgetByDimensionReportTableContext';
import EmailReportModal from './EmailReportModal';
import GenerateBudgetByDimensionReportCriteria from './GenerateBudgetByDimensionReportCriteria';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import SectionTile from 'CommonComponents/SectionTile';
import { useTabsContext } from 'CommonComponents/Tabs';

import useModal from 'Hooks/useModal';
import { useToast } from 'Hooks/useToast';

// The criteria panel submits every field at once, and a double-tapped Generate
// Report would otherwise ask the server twice for the same slot.
const PREVIEW_DEBOUNCE_MS = 400;

// Completion normally arrives on the report channel. This is the fallback for a
// dropped socket — and for local development, where BROADCAST_DRIVER=log means
// nothing is ever delivered.
const PREVIEW_POLL_MS = 10000;

const STATUS_IDLE = 'idle';
const STATUS_CHECKING = 'checking';
const STATUS_GENERATING = 'generating';
const STATUS_READY = 'ready';
const STATUS_FAILED = 'failed';

const EMPTY_PREVIEW = { status: STATUS_IDLE, message: null, payload: null };

/**
 * What to say above the table, if anything. `retry` marks the states the user
 * can only leave by asking for another run.
 */
const previewNotice = (preview) => {
    if (preview.status === STATUS_GENERATING && preview.message) {
        return { type: 'info', role: 'status', message: preview.message, retry: false };
    }

    if (preview.status === STATUS_FAILED) {
        return { type: 'error', role: 'alert', message: preview.message, retry: true };
    }

    // A run that is neither cached, in flight, nor failed has gone: the cache
    // entry expired, or the job never reached a worker.
    if (preview.status === STATUS_IDLE && preview.message) {
        return { type: 'info', role: 'status', message: preview.message, retry: true };
    }

    return null;
};

/**
 * The "Generate Report" tab for both Budgets by Venue and Budgets by Event.
 *
 * The report is built in the background — it exceeds production's request
 * timeout when built inline — so this tab reads the preview cache rather than
 * the report itself:
 *
 *   criteria change → (debounced) GET preview
 *     cached      → render it
 *     generating  → skeleton + notice, wait for the broadcast
 *     not_found   → POST refresh, then wait
 *     failed      → the server's own message, plus Try again
 *
 * Completion arrives on the event's report channel; the poll only covers a
 * socket that never delivers.
 *
 * - Export Report → queues an Excel export and navigates to the Archive tab.
 * - Email Report → opens a modal that POSTs criteria + email to emailReport endpoint.
 */
const GenerateBudgetByDimensionReport = ({ archiveTabId }) => {
    const [criteriaCollapsed, setCriteriaCollapsed] = useState(false);
    const [loadingGenerate, setLoadingGenerate] = useState(false);
    const [loadingEmail, setLoadingEmail] = useState(false);
    const [preview, setPreview] = useState(EMPTY_PREVIEW);

    const { criteria, criteriaVersion, generateReport, sendReportEmail, fetchPreviewCached, refreshPreview, isVikEnabled, dimension, channelName } =
        useBudgetByDimensionReportContext();

    const { handleTabChange } = useTabsContext();

    const toast = useToast();

    const emailReportModal = useModal();

    const isFirstRender = useRef(true);

    // Only the newest request owns the screen: criteria can change while one is
    // still in flight, and its answer describes a slot the user has left.
    const requestSeq = useRef(0);

    // The slot the screen currently stands for, set only while a report is
    // rendered or on its way. A submit landing back on that slot has nothing to
    // do; any other state has to let one through.
    const renderedSlotKey = useRef(null);

    const applyPreview = useCallback((seq, next, slotKey) => {
        if (seq !== requestSeq.current) {
            return;
        }

        renderedSlotKey.current = next.status === STATUS_READY || next.status === STATUS_GENERATING ? slotKey : null;

        setPreview(next);
    }, []);

    const startPreview = useCallback(
        (previewCriteria) => {
            const seq = ++requestSeq.current;
            const slotKey = previewCriteriaCacheKey(previewCriteria);

            renderedSlotKey.current = null;
            setPreview({ status: STATUS_CHECKING, message: null, payload: null });

            return refreshPreview(previewCriteria)
                .then((result) => {
                    applyPreview(
                        seq,
                        {
                            status: STATUS_GENERATING,
                            message: result?.message ?? $t('This report is being generated. It will appear as soon as it is ready.'),
                            payload: null,
                        },
                        slotKey,
                    );
                })
                .catch(() => {
                    applyPreview(
                        seq,
                        {
                            status: STATUS_FAILED,
                            message: $t('The report could not be generated. Please try again, and contact support if it keeps failing.'),
                            payload: null,
                        },
                        slotKey,
                    );
                });
        },
        [applyPreview, refreshPreview],
    );

    /**
     * `foreground` separates the user's own request from a poll tick or a
     * broadcast: only the former queues a run it finds nothing for, and only the
     * former reports a request that never landed. A blip mid-generation must not
     * tear down a run that is still going — the next tick asks again.
     */
    const checkPreview = useCallback(
        (previewCriteria, { foreground = false } = {}) => {
            const seq = ++requestSeq.current;
            const slotKey = previewCriteriaCacheKey(previewCriteria);

            return fetchPreviewCached(previewCriteria)
                .then((result) => {
                    if (result?.status === 'cached') {
                        applyPreview(seq, { status: STATUS_READY, message: null, payload: result }, slotKey);

                        return;
                    }

                    if (result?.status === 'generating') {
                        applyPreview(
                            seq,
                            {
                                status: STATUS_GENERATING,
                                message: result.message ?? $t('This report is being generated. It will appear as soon as it is ready.'),
                                payload: null,
                            },
                            slotKey,
                        );

                        return;
                    }

                    if (result?.status === 'failed') {
                        // Sanitised server-side — the raw reason can carry a
                        // failing SQL statement — so render it as it arrived.
                        applyPreview(
                            seq,
                            {
                                status: STATUS_FAILED,
                                message: result.message ?? $t('The report could not be generated. Please try again, and contact support if it keeps failing.'),
                                payload: null,
                            },
                            slotKey,
                        );

                        return;
                    }

                    if (foreground) {
                        return startPreview(previewCriteria);
                    }

                    applyPreview(seq, { status: STATUS_IDLE, message: result?.message ?? null, payload: null }, slotKey);
                })
                .catch(() => {
                    if (!foreground) {
                        return;
                    }

                    applyPreview(
                        seq,
                        {
                            status: STATUS_FAILED,
                            message: $t('The report could not be generated. Please try again, and contact support if it keeps failing.'),
                            payload: null,
                        },
                        slotKey,
                    );
                });
        },
        [applyPreview, fetchPreviewCached, startPreview],
    );

    // The broadcast and the poll fire outside React's render cycle, so they read
    // the latest criteria and status from refs rather than from a closure that
    // would pin them to the render that subscribed.
    const criteriaRef = useRef(criteria);
    const statusRef = useRef(preview.status);
    const checkPreviewRef = useRef(checkPreview);

    useEffect(() => {
        criteriaRef.current = criteria;
        statusRef.current = preview.status;
        checkPreviewRef.current = checkPreview;
    }, [criteria, preview.status, checkPreview]);

    // Look up the slot the criteria address, then render or queue it.
    //
    // Keyed on the submit counter, not on the criteria: the panel opens
    // pre-filled from the same defaults it submits, so a first Generate on an
    // untouched form changes nothing about the criteria and a key derived from
    // them would leave the button dead. The slot the screen already stands for
    // is what decides whether there is work to do — a Generate that only renamed
    // the run lands back on it, and blanking the table to fetch the report it is
    // already showing is what that check exists to prevent. The name still rides
    // along on any request that does go out, read from the ref so a rename
    // during the debounce is not lost.
    const previewSlotKey = previewCriteriaCacheKey(criteria);

    useEffect(() => {
        // Mount looks the slot up but never queues: arriving on the tab must not
        // start a run nobody asked for. Every later pass comes from a Generate,
        // which does queue when it finds nothing cached.
        const foreground = !isFirstRender.current;
        isFirstRender.current = false;

        if (foreground && previewSlotKey === renderedSlotKey.current) {
            return;
        }

        // Bumped here rather than only when the debounce fires: anything still
        // in flight describes the criteria the user has just left.
        requestSeq.current += 1;
        renderedSlotKey.current = null;
        setPreview({ status: STATUS_CHECKING, message: null, payload: null });

        const timer = setTimeout(() => {
            checkPreviewRef.current(criteriaRef.current, { foreground });
        }, PREVIEW_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [criteriaVersion]);

    useEffect(() => {
        if (preview.status !== STATUS_GENERATING) {
            return;
        }

        const timer = setInterval(() => {
            checkPreviewRef.current(criteriaRef.current, { foreground: false });
        }, PREVIEW_POLL_MS);

        return () => clearInterval(timer);
    }, [preview.status]);

    useEffect(() => {
        if (!channelName || !window.Echo) {
            return;
        }

        const channel = window.Echo.private(channelName);

        channel.listen('.report.generated', () => {
            // Every report on this event broadcasts here, Excel exports
            // included; only a run we are waiting on has anything to collect.
            if (statusRef.current !== STATUS_GENERATING) {
                return;
            }

            checkPreviewRef.current(criteriaRef.current, { foreground: false });
        });

        return () => {
            window.Echo.leave(channelName);
        };
    }, [channelName]);

    const handleGenerateReport = () => {
        setLoadingGenerate(true);
        generateReport()
            .then(() => {
                handleTabChange(archiveTabId);
            })
            .catch(() => {
                toast.error($t('Failed to generate the report. Please try again later.'));
            })
            .finally(() => {
                setLoadingGenerate(false);
            });
    };

    const handleEmailReport = (email) => {
        setLoadingEmail(true);

        sendReportEmail(email)
            .then(() => {
                emailReportModal.hide();
                handleTabChange(archiveTabId);

                toast.success($t('The report has been sent to the specified email address.'));
            })
            .catch(() => {
                setLoadingEmail(false);

                toast.error($t('Failed to send the report. Please try again later.'));
            });
    };

    // The failure is recorded on the run's own row, and the cached endpoint
    // answers from the latest row, so asking again would keep reading the same
    // failure: retry queues a run instead of re-reading.
    const handleRetryPreview = () => startPreview(criteria);

    // The preview is stored keyed differently per dimension:
    // Venue report: { data: ..., venues: [...] }
    // Event report: { data: ..., sports: [...] }
    const reportSheets = preview.payload?.data ?? null;
    const allDimensions = preview.payload?.venues ?? preview.payload?.sports ?? [];

    // When the user has selected specific scope items (venues/sports), only show
    // columns for those selected items — the backend returns all dimensions regardless.
    const scopeIds = criteria?.scope_ids ?? [];
    const dimensions = scopeIds.length > 0 ? allDimensions.filter((dim) => scopeIds.includes(dim.id)) : allDimensions;

    const notice = previewNotice(preview);

    return (
        <div className="tw-flex-auto tw-flex tw-flex-col tw-px-6 tw-py-6.5 tw-rounded-lg tw-bg-white tw-shadow-primary tw-space-y-7 tw-h-full">
            <div className="tw-flex-initial">
                <SectionTile
                    flat
                    header={$t('Include Reports')}
                    collapsed={criteriaCollapsed}
                    onToggle={() => setCriteriaCollapsed(!criteriaCollapsed)}
                    visibly
                >
                    <GenerateBudgetByDimensionReportCriteria />
                </SectionTile>
            </div>

            {notice && (
                <div className="tw-flex-initial">
                    <Alert type={notice.type} role={notice.role}>
                        <div className="tw-flex tw-items-center tw-gap-x-3">
                            <span>{notice.message}</span>
                            {notice.retry && (
                                <Button small variant="outline" onClick={handleRetryPreview}>
                                    {$t('Try again')}
                                </Button>
                            )}
                        </div>
                    </Alert>
                </div>
            )}

            <BudgetByDimensionReportTableProvider
                criteria={criteria}
                report={reportSheets}
                dimensions={dimensions}
                loading={preview.status === STATUS_CHECKING || preview.status === STATUS_GENERATING}
                isVikEnabled={isVikEnabled}
                dimension={dimension}
            >
                <BudgetByDimensionReportTable
                    actions={({ data }) => (
                        <div className="tw-ml-auto tw-flex tw-gap-x-2">
                            <Button onClick={handleGenerateReport} disabled={!data || loadingGenerate} loading={loadingGenerate}>
                                {$t('Export Report')}
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    emailReportModal.show();
                                }}
                                disabled={!data || loadingGenerate || loadingEmail}
                                loading={loadingEmail}
                            >
                                {$t('Email Report')}
                            </Button>
                        </div>
                    )}
                />
            </BudgetByDimensionReportTableProvider>

            <EmailReportModal modal={emailReportModal} onSend={handleEmailReport} loading={loadingEmail} />
        </div>
    );
};

export default GenerateBudgetByDimensionReport;
